import moment from "moment";
import Card from "@mui/material/Card";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import FileDownload from "@mui/icons-material/FileDownload";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import { Box, DataTable, useCommonComponentContext } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import Constants, { USER_ROLE } from "constants/index";
import useDataCommon from "hooks/useDataCommon.hook";
import UserService from "services/identity/user.service";
import TagService from "services/medkit/tag.service";
import ReportService from "services/medkit/report.service";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { BoxValueFilter, FilterForm } from "./FormFilter";
import { ICodename, IRecordReport } from "commons/interfaces";
import { setListPathName } from "store/slice/titleRoute.slice";
import { BooleanInt, PL, ReportStatus } from "constants/enum";
import { setDataAlert, showLoading, showLoadingDataTable } from "store/slice/common.slice";


export interface IDataFilterReport {
    searchText?: string,
    reportStatus?: string,
    departmentId?: string,
    tagIds?: string[],
    endDate?: string | number,
    startDate?: string | number,
    anonymousReporter?: BooleanInt;

    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
};

const userService = new UserService();
const reportService = new ReportService();
const tagService = new TagService();

const MonitoringScreen = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { getAllDepartments } = useDataCommon();
    const { clientId, serviceCode, userInfo } = useCommonComponentContext();

    const dataMapCodenames = useSelector((state: RootState) => state.common.codenames);
    const loadingDataTable = useSelector((state: RootState) => state.common.loadingDataTable);

    const [searchParams] = useSearchParams();
    const [dataGroups, setDataGroups] = useState<ICodename[]>([]);
    const [dataTags, setDataTags] = useState<ICodename[]>([]);
    const [dataFilter, setDataFilter] = useState<IDataFilterReport>({});
    const [dataFilterTemp, setDataFilterTemp] = useState<IDataFilterReport>({});

    const [dataReports, setDataReports] = useState<IRecordReport[]>([]);

    const pageSize = searchParams.get("size");
    const pageNumber = searchParams.get("page");
    const endDate = searchParams.get("to");
    const startDate = searchParams.get("from");
    const searchText = searchParams.get("text");
    const reportStatus = searchParams.get("status");
    const departmentId = searchParams.get("department");
    const tagIds = searchParams.get("tags");
    const anonymousReporter = searchParams.get("anonymousReporter");

    useEffect(() => {
        (async () => {
            const resultGroups = await getAllDepartments({
                clientId: clientId,
                organizationId: userInfo?.userProfile?.organizationId,
            });
            setDataGroups(resultGroups);
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const result = await tagService.getAll();
                const items: ICodename[] = [...result || []].map(element => ({
                    code: element.id,
                    name: element.tagName,
                }));
                setDataTags(items);
            } catch (error) { }
        })();
    }, []);

    useEffect(() => {
        const size = Number(pageSize ?? Constants.ROW_PER_PAGE);
        const page = Number(pageNumber ?? Constants.PAGE_DEFAULT);

        let newEndDate: any = moment().endOf("y").unix();
        let newStartDate: any = moment().startOf("y").unix();
        if (!Helpers.isNullOrEmpty(endDate) && (endDate !== "0")) {
            newEndDate = endDate;
        };
        if (!Helpers.isNullOrEmpty(startDate) && (startDate !== "0")) {
            newStartDate = startDate;
        };

        const newReq: IDataFilterReport = {
            endDate: newEndDate,
            startDate: newStartDate,
            searchText: searchText,
            reportStatus: reportStatus,
            departmentId: departmentId,
            tagIds: !Helpers.isNullOrEmpty(tagIds) ? tagIds.split(",") : undefined,
            pageSize: size,
            pageNumber: page,
        };

        if (!Helpers.isNullOrEmpty(anonymousReporter) && Number(anonymousReporter) === BooleanInt.True) {
            newReq.anonymousReporter = BooleanInt.True;
        }

        loadDataReports(newReq);
    }, [
        pageSize,
        pageNumber,
        endDate,
        startDate,
        searchText,
        departmentId,
        tagIds,
        reportStatus,
        anonymousReporter,
    ]);

    const loadDataReports = async (newReq: IDataFilterReport) => {
        try {
            dispatch(showLoadingDataTable(true));

            const result = await reportService.getPaged({
                endTime: newReq.endDate,
                startTime: newReq.startDate,
                searchText: newReq.searchText,
                reportStatus: newReq.reportStatus,
                isMonitoring: BooleanInt.True,
                incidentPlaces: newReq.departmentId ? [newReq.departmentId] : [],
                tagIds: newReq.tagIds,
                anonymousReporter: newReq.anonymousReporter,
                pageSize: newReq.pageSize,
                pageNumber: newReq.pageNumber,
            });

            setDataFilter(prev => ({ ...prev, ...newReq, totalCount: result?.totalCount ?? 0 }));
            setDataFilterTemp(prev => ({ ...prev, ...newReq, totalCount: result?.totalCount ?? 0 }));

            setDataReports([...result?.items || []]);

        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const handleFilterData = (req: IDataFilterReport) => {
        let query: string = `?page=${req.pageNumber ?? Constants.PAGE_DEFAULT}&size=${req.pageSize ?? Constants.ROW_PER_PAGE}`;

        if (!Helpers.isNullOrEmpty(req.startDate)) {
            query += `&from=${req.startDate}`;
        } else {
            query += `&from=${moment().startOf("y").unix()}`;
        }

        if (!Helpers.isNullOrEmpty(req.endDate)) {
            query += `&to=${req.endDate}`;
        } else {
            query += `&to=${moment().endOf("y").unix()}`;
        }

        if (!Helpers.isNullOrEmpty(req.searchText)) {
            query += `&text=${req.searchText}`;
        }
        if (!Helpers.isNullOrEmpty(req.reportStatus)) {
            query += `&status=${req.reportStatus}`;
        }
        if (!Helpers.isNullOrEmpty(req.departmentId)) {
            query += `&department=${req.departmentId}`;
        }
        if (!Helpers.isNullOrEmpty(req.tagIds) && (req.tagIds?.length || 0) > 0) {
            query += `&tags=${req.tagIds?.join(",")}`;
        }
        if (!Helpers.isNullOrEmpty(req.anonymousReporter)) {
            query += `&anonymousReporter=${req.anonymousReporter}`;
        }

        navigate(Screens.MONITORING + query);
        dispatch(setListPathName({ pathname: Screens.MONITORING, query }));
    };

    const handleCloseReport = (id: string) => {
        Helpers.showConfirmAlert(
            Strings.REPORT.CONFIRM_CLOSE_REPORT,
            async () => {
                try {
                    dispatch(showLoadingDataTable(true));

                    await reportService.updateStatus({
                        id: id,
                        reportStatus: ReportStatus.closed,
                    });

                    await loadDataReports({ ...dataFilter });

                    dispatch(setDataAlert({ message: Strings.REPORT.CLOSE_REPORT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoadingDataTable(false));
                }
            },
            () => { }
        );
    };

    const handleReopenReport = async (id: string) => {
        Helpers.showConfirmAlert(
            Strings.REPORT.CONFIRM_REOPEN_REPORT,
            async () => {
                try {
                    dispatch(showLoadingDataTable(true));

                    await reportService.updateStatus({
                        id: id,
                        reportStatus: ReportStatus.new,
                    });

                    await loadDataReports({ ...dataFilter });

                    dispatch(setDataAlert({ message: Strings.REPORT.REOPEN_REPORT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoadingDataTable(false));
                }
            },
            () => { }
        );
    };

    const handlePrintReport = async (id: string) => {
        Helpers.showConfirmAlert(
            Strings.REPORT.CONFIRM_PRINT_REPORT,
            async () => {
                try {
                    dispatch(showLoading(true));

                    const resultPLIII = await reportService.generatingPL({
                        id: id,
                        reportType: PL.PLIII
                    });

                    const resultPLIV = await reportService.generatingPL({
                        id: id,
                        reportType: PL.PLIV
                    });

                    var fileNamePLIII = `${id}_PLIII.pdf`;
                    const reponseBlobPLIII = new Blob([resultPLIII]);

                    const downloadUrlPLIII = URL.createObjectURL(reponseBlobPLIII);

                    const aPLIII = document.createElement("a");

                    aPLIII.href = downloadUrlPLIII;
                    aPLIII.download = fileNamePLIII;
                    document.body.appendChild(aPLIII);
                    aPLIII.click();

                    //

                    var fileNamePLIV = `${id}_PLIV.pdf`;
                    const reponseBlobPLIV = new Blob([resultPLIV]);

                    const downloadUrlPLIV = URL.createObjectURL(reponseBlobPLIV);

                    const aPLIV = document.createElement("a");

                    aPLIV.href = downloadUrlPLIV;
                    aPLIV.download = fileNamePLIV;
                    document.body.appendChild(aPLIV);
                    aPLIV.click();

                    // Clean up
                    URL.revokeObjectURL(downloadUrlPLIII);
                    URL.revokeObjectURL(downloadUrlPLIV);
                    document.body.removeChild(aPLIV);
                    document.body.removeChild(aPLIII);

                    dispatch(setDataAlert({ message: Strings.REPORT.PRINT_REPORT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoading(false));
                }
            },
            () => { }
        );
    };

    const handleDeleteReport = (id: string) => {
        Helpers.showConfirmAlert(
            Strings.REPORT.CONFIRM_DELETE_REPORT,
            async () => {
                try {
                    dispatch(showLoadingDataTable(true));

                    await reportService.delete(id);

                    await loadDataReports({ ...dataFilter });

                    dispatch(setDataAlert({ message: Strings.REPORT.DELETE_REPORT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoadingDataTable(false));
                }
            },
            () => { },

        );
    };

    const handleExportFile = () => {
        Helpers.showConfirmAlert(
            Strings.REPORT.CONFIRM_EXPORT_FILE_REPORT,
            async () => {
                try {
                    dispatch(showLoading(true));

                    const result = await reportService.generatingAssignReports({
                        serviceCode: serviceCode,
                        organizationId: userInfo?.userProfile?.organizationId,

                        endTime: dataFilter?.endDate,
                        startTime: dataFilter?.startDate,
                        searchText: dataFilter?.searchText,
                        reportStatus: dataFilter?.reportStatus ? [dataFilter?.reportStatus] : [],
                        incidentPlaces: dataFilter?.departmentId ? [dataFilter?.departmentId] : [],
                    });

                    const reponseBlob = new Blob([result]);
                    const fileName = `${moment().unix()}_PhanCong.xlsx`;
                    const downloadUrl = URL.createObjectURL(reponseBlob);

                    const a = document.createElement("a");
                    a.href = downloadUrl;
                    a.download = fileName;

                    document.body.appendChild(a);
                    a.click();

                    URL.revokeObjectURL(downloadUrl);
                    document.body.removeChild(a);

                    dispatch(setDataAlert({ message: Strings.REPORT.EXPORT_FILE_REPORT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoading(false));
                }
            },
            () => { },
        );
    };

    const dataColumns = useMemo(() => ([
        {
            Header: Strings.COMMON.STATUS,
            accessor: "reportStatus",
            width: "80px",
            Cell: ({ value }: any) => {
                const itemStatus = [...dataMapCodenames?.reportStatus || []][Number(value)];
                const colorStatus = Constants.Styles.STATUS_COLOR[Number(value)] || "black";
                return <Box sx={{ color: colorStatus }}>{itemStatus?.name}</Box>
            },
        },
        {
            Header: Strings.REPORT.REPORT_CODE,
            accessor: "reportNo",
            width: "120px",
        },
        {
            Header: Strings.REPORT.CREATE_TIME_REPORT,
            accessor: "createTime",
            width: "100px",
            Cell: ({ value }: any) => {
                return <>{Helpers.dateToString(Number(value), Constants.DateFormat.DDMMYYYY_HHMM)}</>
            },
        },
        {
            Header: Strings.REPORT.DEPARTMENT,
            accessor: "incidentPlaceName",
            Cell: (row: any) => {
                let incidentPlaceName = row.value;

                if (Helpers.isNullOrEmpty(row.value) && !Helpers.isNullOrEmpty(row.row?.original?.incidentPlace)) {
                    incidentPlaceName = dataGroups.find(e => e.code === row.row?.original?.incidentPlace)?.name || "-/-";
                }
                
                return <span title={incidentPlaceName}>{incidentPlaceName}</span>
            },
        },
        {
            Header: Strings.REPORT.REPORTER,
            accessor: "reporterName",
        },
        {
            Header: Strings.ANALYSIS.INCHARGED_STAFF,
            accessor: "inchargedStaffName",
            Cell: (row: any) => {
                return <>{row.value}</>
            },
        },
        {
            Header: Strings.ANALYSIS.DUE_DATE,
            accessor: "duedate",
            width: "130px",
            Cell: ({ value }: any) => {
                return <>{Helpers.dateToString(Number(value), Constants.DateFormat.DDMMYYYY_HHMM)}</>
            },
        },
        {
            Header: Strings.ANALYSIS.SCHEDULE_FINISH_DATE,
            accessor: "scheduleFinishDate",
            width: "120px",
            Cell: ({ value }: any) => {
                return <>{Helpers.dateToString(Number(value), Constants.DateFormat.DDMMYYYY_HHMM)}</>
            },
        },
        {
            Header: Strings.REPORT.DESCRIPTION,
            accessor: "shortDescription",
            Cell: ({ value }: any) => {
                return <span title={value}>{value}</span>
            }
        },
    ]), [
        dataGroups,
        dataMapCodenames?.reportStatus,
    ]);

    return (
        <DashboardLayout
            isPermission
            requiredOrganization
            title={Strings.MONITORING.TITLE}
            route={[{ title: Strings.MONITORING.TITLE, route: "" }]}
            listActions={[
                {
                    color: "info",
                    onPress: handleExportFile,
                    title: (
                        <>
                            {Strings.REPORT.EXPORT_FILE}
                            &nbsp;<FileDownload />
                        </>
                    ),
                }
            ]}
        >
            <Box>
                <BoxValueFilter
                    dataGroups={dataGroups}
                    dataTags={dataTags}
                    dataFilter={dataFilter}
                    dataMapCodenames={dataMapCodenames}
                    handleFilterData={handleFilterData}
                />
                <Card sx={{ padding: 2 }}>
                    <DataTable
                        isShowIndex
                        loading={loadingDataTable}
                        totalCount={dataFilter?.totalCount ?? 0}
                        pageNumber={dataFilter?.pageNumber ?? 1}
                        rowPerPage={dataFilter?.pageSize ?? Constants.ROW_PER_PAGE}
                        onChangePageSize={pageSize => handleFilterData({ ...dataFilter, pageSize })}
                        onChangePageNumber={pageNumber => handleFilterData({ ...dataFilter, pageNumber })}
                        table={{ columns: dataColumns, rows: dataReports ?? [] }}
                        filterForm={
                            <FilterForm
                                dataGroups={dataGroups}
                                dataTags={dataTags}
                                dataFilterTemp={dataFilterTemp}
                                dataMapCodenames={dataMapCodenames}
                                setDataFilterTemp={setDataFilterTemp}
                            />
                        }
                        onCloseFilter={() => { setDataFilterTemp({ ...dataFilter }) }}
                        onReset={() => { handleFilterData({ pageNumber: Constants.PAGE_DEFAULT }); }}
                        onFilter={() => { handleFilterData({ ...dataFilterTemp, pageNumber: Constants.PAGE_DEFAULT }) }}

                        searchText={dataFilter?.searchText}
                        placeholderSearch={Strings.REPORT.SEARCH_TEXT}
                        onSearch={(value) => {
                            handleFilterData({
                                ...dataFilter,
                                searchText: value,
                                pageNumber: Constants.PAGE_DEFAULT,
                            });
                        }}
                        onSearchChange={(value) => {
                            setDataFilter(prev => ({
                                ...prev,
                                searchText: value,
                                pageNumber: Constants.PAGE_DEFAULT,
                            }));
                        }}

                        actionList={(row) => {
                            if (row.reportStatus === ReportStatus.closed) {
                                if (userInfo?.userProfile?.roleCode === USER_ROLE.ADMIN) {
                                    // dropdownAdminRpClosed
                                    return ([
                                        {
                                            key: "assign",
                                            icon: <FormatListBulleted />,
                                            title: Strings.ASSIGN.ASSIGN,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.ASSIGNMENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "edit",
                                            iconName: "edit",
                                            title: Strings.COMMON.EDIT,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.REPORT_INCIDENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "analysis",
                                            title: Strings.ANALYSIS.ANALYSIS,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.REPORT_ANALYSIS}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "reopen",
                                            title: Strings.REPORT.REOPEN_REPORT,
                                            callback: (row) => { handleReopenReport(row.id); },
                                        },
                                        {
                                            key: "print",
                                            title: Strings.REPORT.PRINT_REPORT,
                                            callback: (row) => { handlePrintReport(row.id); },
                                        },
                                        {
                                            key: "delete",
                                            title: Strings.COMMON.DELETE,
                                            callback: (row) => { handleDeleteReport(row.id); },
                                        },
                                    ])
                                } else {
                                    // dropdownView
                                    return ([
                                        {
                                            key: "assign",
                                            icon: <FormatListBulleted />,
                                            title: Strings.ASSIGN.ASSIGN,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.ASSIGNMENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "view",
                                            iconName: "view",
                                            title: Strings.COMMON.VIEW_DETAIL,
                                            callback: (row) => {
                                                navigate(`${Screens.MONITORING}${Screens.REPORT_INCIDENT}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "view_analysis",
                                            title: Strings.ANALYSIS.VIEW_ANALYSIS,
                                            callback: (row) => {
                                                navigate(`${Screens.MONITORING}${Screens.REPORT_ANALYSIS}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "print",
                                            title: Strings.REPORT.PRINT_REPORT,
                                            callback: (row) => { handlePrintReport(row.id); },
                                        },
                                    ])
                                }
                            } else {
                                if (userInfo?.userProfile?.roleCode === USER_ROLE.ADMIN) {
                                    // dropdownAdmin
                                    return ([
                                        {
                                            key: "assign",
                                            icon: <FormatListBulleted />,
                                            title: Strings.ASSIGN.ASSIGN,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.ASSIGNMENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "edit",
                                            iconName: "edit",
                                            title: Strings.COMMON.EDIT,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.REPORT_INCIDENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "analysis",
                                            title: Strings.ANALYSIS.ANALYSIS,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.REPORT_ANALYSIS}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "close",
                                            title: Strings.REPORT.CLOSE_REPORT,
                                            callback: (row) => { handleCloseReport(row.id); },
                                        },
                                        {
                                            key: "print",
                                            title: Strings.REPORT.PRINT_REPORT,
                                            callback: (row) => { handlePrintReport(row.id); },
                                        },
                                        {
                                            key: "delete",
                                            title: Strings.COMMON.DELETE,
                                            callback: (row) => { handleDeleteReport(row.id); },
                                        },
                                    ])
                                } else {
                                    // dropdownReporter
                                    return ([
                                        {
                                            key: "assign",
                                            icon: <FormatListBulleted />,
                                            title: Strings.ASSIGN.ASSIGN,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.ASSIGNMENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "edit",
                                            iconName: "edit",
                                            title: Strings.COMMON.EDIT,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.REPORT_INCIDENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "analysis",
                                            title: Strings.ANALYSIS.ANALYSIS,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.MONITORING}${Screens.REPORT_ANALYSIS}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "print",
                                            title: Strings.REPORT.PRINT_REPORT,
                                            callback: (row) => { handlePrintReport(row.id); },
                                        },
                                    ])
                                }
                            };
                        }}
                    />
                </Card>
            </Box>
        </DashboardLayout>
    );
};

export default MonitoringScreen;
